import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { LOGO_NAVY, LOGO_WHITE } from "@/lib/brand";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@xlentpay.com");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Left: form */}
      <div className="flex w-full flex-col justify-center px-6 py-12 sm:px-12 lg:w-1/2 lg:px-20">
        <div className="mx-auto w-full max-w-sm animate-fade-up">
          <img src={LOGO_NAVY} alt="XlentPay" className="mb-12 h-9 w-auto" />

          <h1 className="font-heading text-3xl font-semibold tracking-tight">Welcome back</h1>
          <p className="mt-2 text-sm text-muted-foreground">Sign in to your business intelligence dashboard.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                placeholder="you@company.com"
                className="h-11"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  placeholder="Enter your password"
                  className="h-11 pr-11"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  data-testid="toggle-password"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && (
              <div className="rounded-md bg-destructive/10 px-3 py-2.5 text-sm text-destructive" data-testid="login-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="brand-gradient flex h-11 w-full items-center justify-center rounded-md text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:brightness-105 disabled:opacity-60"
              style={{ textShadow: "0 1px 2px rgba(0,0,0,0.35)" }}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign in
            </button>
          </form>
        </div>
      </div>

      {/* Right: brand panel */}
      <div className="relative hidden overflow-hidden lg:block lg:w-1/2" style={{ backgroundColor: "var(--navy)" }}>
        <div className="brand-gradient absolute -right-24 -top-24 h-96 w-96 rounded-full opacity-20 blur-3xl" />
        <div className="brand-gradient absolute -bottom-32 -left-16 h-96 w-96 rounded-full opacity-15 blur-3xl" />
        <div className="relative flex h-full flex-col justify-between p-14">
          <img src={LOGO_WHITE} alt="XlentPay" className="h-8 w-auto" />
          <div>
            <p className="max-w-md font-heading text-3xl font-semibold leading-snug text-white">
              Every client, every referral — in one place.
            </p>
            <p className="mt-3 max-w-md text-sm text-white/70">
              Import your weekly export and instantly see referral performance, KYC status, and client growth.
            </p>
            <div className="brand-gradient mt-8 h-1 w-24 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
}
